import type { CloneableConversationSettings } from '../Conversation';
import { defaultReasoningEffortForModel, Model, ReasoningEffort } from '../types';

import Flags from 'js/src/libs/flags';

export const SUBAGENT_TYPES = ['general', 'codeo', 'chart', 'graphic'] as const;
export type SubagentType = (typeof SUBAGENT_TYPES)[number];

export const DEFAULT_SUBAGENT_TYPE: SubagentType = 'general';

/**
 * The subagent kinds this user may start. `codeo` sits behind the codeo flag, since without it the
 * subagent gets no authoring tools, and `chart` and `graphic` behind the codeo subagents flag.
 */
export function getAllowedSubagentTypes(): SubagentType[] {
    return SUBAGENT_TYPES.filter(
        (type) =>
            (type !== 'codeo' || Flags.canUseCodeo()) &&
            (!designsOneScene(type) || Flags.canUseCodeoSubagents()),
    );
}

export interface SubagentModelSettings {
    model: string;
    reasoningEffort: string;
}

/**
 * `general` is a clone, so it runs on whatever the parent is set to right now. The others pin a
 * model suited to their work, whatever the chat is set to.
 */
export function getSubagentModelSettings(
    type: SubagentType,
    parent: CloneableConversationSettings,
): SubagentModelSettings {
    switch (type) {
        case 'codeo':
            return {
                model: Model.DEEPSEEK_V4_1_FLASH_1017,
                reasoningEffort: ReasoningEffort.MEDIUM,
            };
        case 'chart':
        case 'graphic':
            return { model: Model.CLAUDE_OPUS_5_5, reasoningEffort: ReasoningEffort.MEDIUM };
        case 'general':
            return {
                model: parent.model,
                reasoningEffort:
                    parent.reasoningEffort ??
                    defaultReasoningEffortForModel(parent.model) ??
                    ReasoningEffort.NONE,
            };
    }
}

/** Kinds that design one scene, given by scene_id, through a single write tool bound to it. */
export function designsOneScene(type: SubagentType): type is 'chart' | 'graphic' {
    return type === 'chart' || type === 'graphic';
}
